import React from 'react'
import { api } from '../api/client'
import type { ClientForm, FormFrequency } from '../api/client'
import type { ClientDetail, Preference } from '../types/app'
import FormChip from '../forms/FormChip'
import { DEFAULT_REFRESH_MONTHS, FREQUENCY_CHOICES, refreshMonthsFor, sendFormMessage } from '../forms/status'
import { useApp } from '../context/AppContext'
import { formatDay } from '../catalog/format'
import Field from './settings/Field'
import { saveErrorMessage, showToast } from './settings/toast'
import FormSubmissionSheet from './FormSubmissionSheet'
import './ClientForms.css'

interface Props {
  client: ClientDetail
  onClientChange: (client: ClientDetail) => void
  onPreferencesChange?: (preferences: Preference[]) => void
}

// ClientForms is the profile's Forms section: each form she has asked this
// client for, its signed copies newest first, and how often the forms are asked.
export default function ClientForms({ client, onClientChange, onPreferencesChange }: Props) {
  const { dispatch } = useApp()
  const [forms, setForms] = React.useState<ClientForm[] | null>(null)
  const [busy, setBusy] = React.useState<string | null>(null)
  const [error, setError] = React.useState('')
  const [openSubmission, setOpenSubmission] = React.useState<string | null>(null)
  const [frequencyError, setFrequencyError] = React.useState('')
  const [savingFrequency, setSavingFrequency] = React.useState(false)
  const [months, setMonths] = React.useState(String(client.form_refresh_months ?? DEFAULT_REFRESH_MONTHS))

  const load = React.useCallback(() => {
    api.listClientForms(client.id).then(setForms).catch(() => setForms([]))
  }, [client.id])
  React.useEffect(() => {
    load()
  }, [load])

  const act = async (templateId: string, run: () => Promise<void>, done: string) => {
    setBusy(templateId)
    setError('')
    try {
      await run()
      showToast(dispatch, done)
      load()
    } catch (e) {
      setError(sendFormMessage(e))
    } finally {
      setBusy(null)
    }
  }

  const saveFrequency = async (frequency: FormFrequency, refresh: number | null) => {
    setSavingFrequency(true)
    setFrequencyError('')
    try {
      onClientChange(await api.updateClient(client.id, { form_frequency: frequency, form_refresh_months: refresh }))
    } catch (e) {
      setFrequencyError(saveErrorMessage(e))
    } finally {
      setSavingFrequency(false)
    }
  }

  const frequency = client.form_frequency ?? 'first_visit'
  const first = client.first_name

  return (
    <section className="profile-section" aria-label="Forms">
      <h2>Forms</h2>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {forms && forms.length === 0 && <p className="field-hint">No forms yet. Forms you set on a service are asked of {first} when they book.</p>}
      {forms?.map((f) => (
        <div key={f.template_id} className="client-form">
          <div className="client-form__head">
            <p className="client-form__name">{f.template_name}</p>
            <FormChip status={f.status} />
          </div>
          {f.submissions.length > 0 && (
            <ul className="client-form__list">
              {f.submissions.map((s) => (
                <li key={s.id}>
                  <button type="button" className="btn btn-quiet btn-compact" onClick={() => setOpenSubmission(s.id)}>
                    Signed {formatDay(s.signed_at.slice(0, 10))}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="client-form__actions">
            <button
              type="button"
              className="btn btn-secondary btn-compact"
              disabled={busy === f.template_id}
              onClick={() =>
                act(f.template_id, () => api.sendClientForm(client.id, f.template_id), `Form emailed to ${first}`)
              }
            >
              {f.status === 'needed' ? 'Ask again' : 'Send form'}
            </button>
            {f.status === 'needed' && (
              <button
                type="button"
                className="btn btn-quiet btn-compact"
                disabled={busy === f.template_id}
                onClick={() => act(f.template_id, () => api.stopClientForm(client.id, f.template_id), 'Stopped asking')}
              >
                Stop asking
              </button>
            )}
          </div>
        </div>
      ))}

      <Field
        id="form-frequency"
        label="Form frequency"
        error={frequencyError || undefined}
        hint="How often the forms are asked of this client."
      >
        <select
          id="form-frequency"
          value={frequency}
          disabled={savingFrequency}
          onChange={(e) => {
            const next = e.target.value as FormFrequency
            saveFrequency(next, refreshMonthsFor(next, Number(months) || DEFAULT_REFRESH_MONTHS))
          }}
        >
          {FREQUENCY_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>
      {frequency === 'months' && (
        <Field id="form-refresh-months" label="Ask again after (months)">
          <input
            id="form-refresh-months"
            type="number"
            inputMode="numeric"
            min={1}
            max={60}
            value={months}
            disabled={savingFrequency}
            onChange={(e) => setMonths(e.target.value)}
            onBlur={() => {
              const n = Math.round(Number(months))
              if (n >= 1 && n <= 60 && n !== client.form_refresh_months) saveFrequency('months', n)
            }}
          />
        </Field>
      )}

      {openSubmission && (
        <FormSubmissionSheet
          submissionId={openSubmission}
          clientId={client.id}
          onClose={() => setOpenSubmission(null)}
          onPreferencesChange={onPreferencesChange}
        />
      )}
    </section>
  )
}
